import React, { useState, useRef } from "react";
import { useSubscription } from "../contexts/SubscriptionContext";
import SubscribeButton from "./SubscribeButton";
import { motion, AnimatePresence } from "framer-motion";

interface TopicInputProps {
  onSubmit: (topic: string) => Promise<void>;
  onYouTubeSubmit: (url: string) => Promise<void>;
  isLoading?: boolean;
}

const SUGGESTIONS = [
  { label: "Quantum Physics", icon: "⚛️" },
  { label: "Machine Learning", icon: "🤖" },
  { label: "The Roman Empire", icon: "🏛️" },
  { label: "How DNA Works", icon: "🧬" },
  { label: "Climate Change", icon: "🌍" },
  { label: "World War II", icon: "🗺️" },
];

const YOUTUBE_URL_REGEX = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^"&?\/\s]{11})/i;

const TopicInput: React.FC<TopicInputProps> = ({
  onSubmit,
  onYouTubeSubmit,
  isLoading = false,
}) => {
  const [mode, setMode] = useState<"topic" | "youtube">("topic");
  const [topic, setTopic] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const { isActive, isLoading: subscriptionLoading } = useSubscription();
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isValidYouTubeUrl = (url: string) => YOUTUBE_URL_REGEX.test(url);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate input first, then gate generation behind an active subscription.
    if (mode === "youtube") {
      if (!youtubeUrl.trim() || !isValidYouTubeUrl(youtubeUrl)) return;
    } else {
      if (!topic.trim()) return;
    }

    // Not subscribed → show the paywall instead of generating.
    if (!isActive) {
      setShowSubscribeModal(true);
      return;
    }

    if (mode === "youtube") {
      await onYouTubeSubmit(youtubeUrl.trim());
      setYoutubeUrl("");
    } else {
      await onSubmit(topic);
      setTopic("");
    }
  };

  const handleSuggestion = (label: string) => {
    setTopic(label);
    inputRef.current?.focus();
  };

  const handleModeSwitch = (newMode: "topic" | "youtube") => {
    setMode(newMode);
    setTopic("");
    setYoutubeUrl("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const isSubmitDisabled =
    isLoading ||
    subscriptionLoading ||
    (mode === "topic" ? !topic.trim() : !youtubeUrl.trim() || !isValidYouTubeUrl(youtubeUrl));

  const youtubeUrlInvalid = mode === "youtube" && youtubeUrl.trim() !== "" && !isValidYouTubeUrl(youtubeUrl);

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Mode toggle */}
      <div className="flex gap-1 p-1 bg-slate-100 dark:bg-gray-900 rounded-xl mb-4 w-fit mx-auto">
        <button
          type="button"
          onClick={() => handleModeSwitch("topic")}
          className={`px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-2
            ${mode === "topic"
              ? "bg-white dark:bg-gray-800 text-slate-900 dark:text-white shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            }`}
        >
          <i className="fas fa-book-open text-sm"></i>
          Topic
        </button>
        <button
          type="button"
          onClick={() => handleModeSwitch("youtube")}
          className={`px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-2
            ${mode === "youtube"
              ? "bg-white dark:bg-gray-800 text-slate-900 dark:text-white shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            }`}
        >
          <i className="fab fa-youtube text-sm text-red-500"></i>
          YouTube
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="relative">
          {mode === "topic" ? (
            <input
              ref={inputRef}
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="What would you like to learn about today?"
              className="w-full px-6 py-5 pr-44 rounded-2xl border border-slate-200 dark:border-gray-700
                      bg-white dark:bg-gray-900
                      text-slate-900 dark:text-white text-lg
                      focus:border-[#47b3fe] focus:outline-none focus:ring-4 focus:ring-[#47b3fe]/10 dark:focus:ring-[#47b3fe]/10
                      placeholder-slate-400 dark:placeholder-slate-600
                      shadow-card transition-all duration-200"
              disabled={isLoading || subscriptionLoading}
            />
          ) : (
            <input
              ref={inputRef}
              type="url"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className={`w-full px-6 py-5 pr-44 rounded-2xl border
                      bg-white dark:bg-gray-900
                      text-slate-900 dark:text-white text-lg
                      focus:outline-none focus:ring-4
                      placeholder-slate-400 dark:placeholder-slate-600
                      shadow-card transition-all duration-200
                      ${youtubeUrlInvalid
                        ? "border-red-400 dark:border-red-600 focus:border-red-400 focus:ring-red-400/10"
                        : "border-slate-200 dark:border-gray-700 focus:border-[#47b3fe] focus:ring-[#47b3fe]/10"
                      }`}
              disabled={isLoading || subscriptionLoading}
            />
          )}
          <button
            type="submit"
            disabled={isSubmitDisabled}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 px-5 py-3
                   bg-[#47b3fe] hover:bg-blue-500 text-white text-base font-semibold rounded-xl
                   disabled:opacity-40 disabled:cursor-not-allowed
                   transition-all duration-200 flex items-center gap-2 shadow-sm"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
            ) : (
              <>
                <i className="fas fa-bolt text-sm"></i>
                Generate
              </>
            )}
          </button>
        </div>
        {youtubeUrlInvalid && (
          <p className="mt-1.5 text-xs text-red-500 dark:text-red-400 pl-1">
            Please enter a valid YouTube URL
          </p>
        )}
      </form>

      {/* Suggestion chips — only in topic mode */}
      {mode === "topic" && (
        <div className="flex flex-wrap gap-2.5 mt-4 justify-center">
          {SUGGESTIONS.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => handleSuggestion(s.label)}
              disabled={isLoading || subscriptionLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium
                         text-slate-500 dark:text-slate-400
                         bg-white dark:bg-gray-900
                         border border-slate-200 dark:border-gray-800
                         rounded-full
                         hover:border-[#47b3fe] hover:text-[#47b3fe]
                         dark:hover:border-[#47b3fe] dark:hover:text-[#47b3fe]
                         transition-colors duration-150 disabled:opacity-40"
            >
              <span className="text-base">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Premium upsell */}
      {!isActive && !subscriptionLoading && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-12 rounded-2xl overflow-hidden border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-card"
        >
          <div className="px-7 pt-6 pb-5 flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0 border border-slate-100 dark:border-gray-800">
              <img
                src="/albert.jpg"
                alt="Albert Einstein"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Unlock All Learning Features
              </h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                Subscribe to generate unlimited custom learning materials
              </p>
            </div>
          </div>
          <div className="px-7 pb-6">
            <SubscribeButton />
          </div>
        </motion.div>
      )}

      {/* Subscription modal */}
      <AnimatePresence>
        {showSubscribeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowSubscribeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-900 w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border border-slate-200 dark:border-gray-800"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-[#47b3fe] to-indigo-600 px-6 py-5">
                <h3 className="text-base font-bold text-white">
                  Premium Feature
                </h3>
                <p className="text-blue-100 text-sm mt-1">
                  You're one step away from unlimited learning.
                </p>
              </div>
              <div className="p-6">
                <p className="text-slate-600 dark:text-slate-300 text-sm mb-5">
                  {mode === "youtube" ? (
                    <>Subscribe to generate learning content from YouTube videos.</>
                  ) : (
                    <>
                      Your topic{" "}
                      <span className="font-semibold text-slate-900 dark:text-white">
                        "{topic}"
                      </span>{" "}
                      looks interesting! Subscribe to generate custom learning content.
                    </>
                  )}
                </p>
                <SubscribeButton />
                <button
                  onClick={() => setShowSubscribeModal(false)}
                  className="w-full mt-3 py-2.5 text-sm text-slate-400 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400 transition-colors font-medium"
                >
                  Maybe Later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TopicInput;
